import React, { useEffect, useState } from 'react';
import { Form, FormField, useForm, useWatch } from '@/components/form';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  InputGroupTextarea,
} from '@/components/ui/input-group';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Spinner } from '@/components/ui/spinner';
import useSupportHandler from '../controllers/useSupportHandler';
import Icon from './Icon';
import { useUserInfoQuery } from '../../services/api';

function SupportAndFeedback({ isDrawerOpen, setIsDrawerOpen, supportType }) {
  const { supportHandler, feedbackHandler, isLoading } = useSupportHandler();
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [form] = useForm();
  const values = useWatch([], form);
  const { data: userInfo } = useUserInfoQuery();

  const isFeedback = supportType === 'Feedback';
  const drawerTitle = isFeedback ? 'Send Feedback' : 'Contact Support';
  const drawerDescription = isFeedback
    ? 'Share your thoughts and ideas to help us improve Bridged.'
    : 'Describe the issue you’re facing and our team will get back to you soon.';
  const submitLabel = isFeedback ? 'Send Feedback' : 'Send Request';
  const tipText = isFeedback
    ? 'Specific feedback about what worked or didn’t works best. Screenshots and examples are helpful.'
    : 'Include relevant page URLs, steps to reproduce, and any error messages you saw.';

  useEffect(() => {
    if (!isDrawerOpen) {
      return;
    }

    const firstname = userInfo?.data?.firstname || '';
    const lastname = userInfo?.data?.lastname || '';
    const fullName = `${firstname} ${lastname}`.trim();

    if (fullName) {
      form.setFieldsValue({ name: fullName });
    }
  }, [isDrawerOpen, userInfo, form]);

  useEffect(() => {
    // The form only mounts with the drawer; validating it on every page load just warns.
    if (!isDrawerOpen) return;
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [values, form, isDrawerOpen]);

  const handleClose = () => {
    form.resetFields();
    setIsDrawerOpen(false);
  };

  const handleSubmit = valuesToSubmit => {
    if (isFeedback) {
      feedbackHandler(valuesToSubmit, setIsDrawerOpen);
    } else {
      supportHandler(valuesToSubmit, setIsDrawerOpen);
    }
  };

  return (
    <Sheet
      open={isDrawerOpen}
      onOpenChange={next => {
        if (!next) handleClose();
      }}
    >
      <SheetContent className="gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-[460px]">
        <SheetHeader className="gap-1 border-b px-6 pt-5 pb-4 pe-14">
          <SheetTitle className="text-base leading-[1.35] font-medium">{drawerTitle}</SheetTitle>
          <SheetDescription className="text-[0.8125rem] leading-[1.45]">
            {drawerDescription}
          </SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-3 pb-6">
          <Form form={form} autoComplete="off" requiredMark={false} onFinish={handleSubmit}>
            <FieldGroup className="gap-4">
              <FormField
                label="Name"
                name="name"
                labelClassName="text-[0.8125rem] leading-[1.35] text-muted-foreground"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input placeholder="Your full name" />
              </FormField>

              <FormField
                label="Subject"
                name="subject"
                labelClassName="text-[0.8125rem] leading-[1.35] text-muted-foreground"
                rules={[{ required: true, message: 'Please enter a subject' }]}
              >
                <Input
                  placeholder={
                    isFeedback ? 'What is your feedback about?' : 'Brief summary of the issue'
                  }
                />
              </FormField>

              <FormField
                label="Message"
                name="body"
                bind="input"
                labelClassName="text-[0.8125rem] leading-[1.35] text-muted-foreground"
                rules={[
                  { required: true, message: 'Please enter a message' },
                  { min: 10, message: 'Message should be at least 10 characters' },
                ]}
              >
                {control => (
                  <InputGroup>
                    <InputGroupTextarea
                      {...control}
                      placeholder={
                        isFeedback
                          ? 'Tell us what you liked, what could be better, or any ideas you have…'
                          : 'Describe the problem, what you expected, and any steps we can follow…'
                      }
                      maxLength={2000}
                      className="max-h-[calc(10lh+1.25rem)] min-h-[calc(6lh+1.25rem)]"
                    />
                    <InputGroupAddon align="block-end">
                      <InputGroupText className="ms-auto text-xs tabular-nums">
                        {[...control.value].length} / 2000
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                )}
              </FormField>
            </FieldGroup>

            <div className="mt-5 flex items-start gap-2.5 rounded-lg bg-muted/50 px-4 py-3.5">
              <Icon name="InfoCircleOutlined" color="primary" style={{ width: 12, marginTop: 3 }} />
              <span className="text-xs leading-[1.45] text-muted-foreground">
                <strong>Tip:</strong> {tipText}
              </span>
            </div>
          </Form>
        </div>
        <SheetFooter className="flex-row justify-end gap-3 border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="lg"
            disabled={!isSubmittable || isLoading}
            onClick={() => form.submit()}
          >
            {isLoading && <Spinner data-icon="inline-start" />}
            {submitLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default SupportAndFeedback;
